"use client";

/**
 * Everything about a TASK's composer that the pill does not carry on other
 * screens, in one leaf BashPill mounts only in task mode (spec 2026-09-20
 * one-composer-everywhere §4, R-F2): the @ and / pickers on whichever editor
 * the pill has, the slash expansion at submit, the task's own prefill lane, the
 * desk's typing signal, the task-aware pre-warm, the focus on a task switch and
 * the highlight. Every rule here is the task box's own code, reused
 * (useMentionSkillPickers, expandSlashOnSubmit, applyPrefill, useTaskPrewarm,
 * useComposerRoster), never a copy.
 *
 * It is the ONLY part of the pill that needs the auth provider (the roster and
 * the skill index), which is why it is a component and not hooks inside
 * BashPill: the pill renders provider-free on every other route. It draws the
 * two dropdowns and nothing else.
 *
 * Mounted on the whole task ROUTE, the new-task page included (R-F14): the old
 * new-task box had both lists and the slash expansion, and the three "Create
 * with Bash" buttons prefill a bare slash that only the web ever expands. With
 * no task yet (`taskId` null) the lists, the expansion, the highlight and the
 * pre-warm all work (useTaskPrewarm falls back to the pending new-task id, the
 * way the old box's did); what is keyed on a CREATED task (the desk's typing
 * signal, the per-task prefill lane, the lanes on the mention list) stands down.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  type RefObject,
} from "react";
import { MentionDropdown } from "@/components/message-input/MentionDropdown";
import { SkillDropdown } from "@/components/message-input/SkillDropdown";
import { useMentionSkillPickers } from "@/components/message-input/useMentionSkillPickers";
import { expandSlashOnSubmit } from "@/components/message-input/slash-submit";
import { applyPrefill } from "@/components/message-input/prefill";
import { useComposerRoster } from "@/hooks/useComposerRoster";
import { useTaskPrewarm } from "@/hooks/useTaskPrewarm";
import { useSession } from "@/hooks/useSession";
import { useActiveSkillIndex } from "@/hooks/useActiveSkillIndex";
import { useComposerDraftStore } from "@/stores/composerDraftStore";
import { setComposerTyping } from "@/stores/deskSignals";
import {
  clearEditorHighlights,
  paintEditorHighlights,
} from "./editor-highlights";

/** How BashPill lends the leaf its editor, whichever one it is. */
export interface PillEditorPort {
  /** The element that has focus while typing: the lists anchor to it and hear its keys. */
  anchorRef: RefObject<HTMLElement | null>;
  /** The textarea, when the editor IS one (the pickers place the caret in it
   *  after a pick); a ref that stays null for the rich editor, whose own
   *  setText lands the caret at the end. */
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  /** The caret's offset in the words as sent, read at call time. */
  caretOffset: () => number;
  /** The caret's rect for an editor that is not a textarea; omitted for the textarea. */
  caretRect?: () => DOMRect | null;
  /** Write the words into the editor AND the draft (a pick or a prefill rewrote them). */
  write: (next: string) => void;
  focus: () => void;
}

export interface PillTaskPickersHandle {
  /** A bare /skill expanded to /ability:skill, at submit only. */
  expandSlash(trimmed: string): string;
}

export const PillTaskPickers = forwardRef<
  PillTaskPickersHandle,
  {
    /** The created task in view, or null on the new-task page (no task yet:
     *  a pending placeholder is not a created task). */
    taskId: string | null;
    /** The field's words, as sent. */
    text: string;
    /** The field is in hand. */
    focused: boolean;
    /** The lane whose screen has the column, or null for Bash. */
    laneInView: string | null;
    port: PillEditorPort;
  }
>(function PillTaskPickers({ taskId, text, focused, laneInView, port }, ref) {
  // The port is a fresh object every render of the host; effects and picks
  // read it through a ref so none of them re-runs, or re-subscribes, for its
  // identity. Written in an effect (react-hooks/refs bars a write during
  // render) that is declared FIRST: effects run in order, so every effect
  // below reads this commit's port and words.
  const portRef = useRef(port);
  const textRef = useRef(text);
  useEffect(() => {
    portRef.current = port;
    textRef.current = text;
  });
  // Stable, so a pick handler changes with the words and never with the port:
  // the dropdowns re-add their key listener whenever it does.
  const write = useCallback((next: string) => portRef.current.write(next), []);

  const roster = useComposerRoster();
  const { session } = useSession();
  const skillIndex = useActiveSkillIndex(session?.user?.id ?? null);
  useImperativeHandle(
    ref,
    () => ({
      expandSlash: (trimmed: string) =>
        expandSlashOnSubmit(trimmed, skillIndex),
    }),
    [skillIndex]
  );

  const pickers = useMentionSkillPickers({
    message: text,
    setMessage: write,
    textareaRef: port.textareaRef,
    agents: roster.agents,
    skills: roster.skills,
    createdTaskId: taskId,
    laneInView,
  });
  // Neither of the two effects below runs at MOUNT, which is where the pill
  // differs from the box: the box runs both only from a change event, while
  // the pill's words are store-held (homeDraft) and survive the door shutting,
  // so the leaf often mounts over a parked draft. Scanning there opens a list
  // nobody asked for and the next Enter picks; pre-warming there wakes the box
  // with no keystroke, and the box is awake only while there is work. A
  // prefill or a pick MOVES the words, so both still run for those.
  const seen = useRef(text);

  // The words moved: look for a trigger at the caret. An effect, not a change
  // handler, so it serves both editors; the caret is read AFTER the commit,
  // where it already stands behind the character just typed.
  const scan = pickers.scan;
  useEffect(() => {
    if (seen.current === text) return;
    scan(text, portRef.current.caretOffset());
  }, [text, scan]);

  // The task-aware typing pre-warm (the box's own): it carries THIS task, so
  // the sandbox pre-spawns a warm query for it. Throttled inside the hook.
  // The LAST of the two effects keyed on the words marks them seen.
  const preWarm = useTaskPrewarm();
  useEffect(() => {
    if (seen.current === text) return;
    seen.current = text;
    if (text.length > 0) preWarm();
  }, [text, preWarm]);

  // The desk's "listening" (task-story/Desk.tsx): words in the field with the
  // field in hand. Taken back the moment the draft is sent, emptied or
  // abandoned, and on a task switch or unmount (the cleanup closes over the
  // task being LEFT), or the face would keep listening to nobody.
  // With no task yet there is nobody to listen to (the box's own rule).
  useEffect(() => {
    if (taskId) setComposerTyping(taskId, focused && text.trim().length > 0);
  }, [taskId, focused, text]);
  useEffect(() => {
    if (!taskId) return;
    return () => setComposerTyping(taskId, false);
  }, [taskId]);

  // The task's own prefill lane (plan ruling P-7): the shelf's take-back, the
  // Try chip, Studio's retry and "Set as ref", the door's handed-back words.
  // Merged onto the LIVE words by the box's own applyPrefill, spent once.
  // No task, no lane: the new-task page's prefills ride the HOME lane, which
  // BashPill takes itself (TaskDockStrip moves a new-task draft onto it).
  const prefill = useComposerDraftStore((s) =>
    taskId ? s.prefillByTask[taskId] : undefined
  );
  const consumePrefill = useComposerDraftStore((s) => s.consumePrefill);
  useEffect(() => {
    if (!prefill || !taskId) return;
    portRef.current.write(
      applyPrefill(textRef.current, prefill.text, prefill.mode)
    );
    consumePrefill(taskId);
    portRef.current.focus();
  }, [prefill, taskId, consumePrefill]);

  // Switching task takes the field in hand so the person can type at once,
  // unless they are typing somewhere else (the box's own rule).
  useEffect(() => {
    const active = document.activeElement;
    const anchor = portRef.current.anchorRef.current;
    const typingElsewhere =
      active instanceof HTMLElement &&
      active !== anchor &&
      (active.tagName === "INPUT" ||
        active.tagName === "TEXTAREA" ||
        active.isContentEditable);
    if (typingElsewhere) return;
    const id = requestAnimationFrame(() => portRef.current.focus());
    return () => cancelAnimationFrame(id);
  }, [taskId]);

  // The highlight, on the editor's own text nodes. Slugs only: the painter
  // needs no more, and a stable pair of lists keeps this effect quiet.
  const agentSlugs = pickers.mention.roster;
  const skillSlugs = pickers.skill.roster;
  useEffect(() => {
    paintEditorHighlights(
      portRef.current.anchorRef.current,
      agentSlugs,
      skillSlugs
    );
  }, [text, agentSlugs, skillSlugs]);
  useEffect(() => () => clearEditorHighlights(), []);

  return (
    <>
      <MentionDropdown
        open={pickers.mention.open}
        agents={pickers.mention.agents}
        anchorRef={port.anchorRef}
        triggerPos={pickers.mention.triggerPos}
        caretRect={port.caretRect}
        zIndex="z-80"
        onSelect={pickers.mention.onSelect}
        onClose={pickers.mention.onClose}
      />
      <SkillDropdown
        open={pickers.skill.open}
        skills={pickers.skill.skills}
        anchorRef={port.anchorRef}
        triggerPos={pickers.skill.triggerPos}
        caretRect={port.caretRect}
        zIndex="z-80"
        onSelect={pickers.skill.onSelect}
        onClose={pickers.skill.onClose}
      />
    </>
  );
});
